import { Injectable, Signal, computed, signal } from '@angular/core';
import { DashboardProposal, ProposalRevisionDraft } from '../ai-store/ai-chat.types';

/** An assistant's revision of the open proposal, with the proposal it answers (what was sent, not what the form holds now). */
export interface ProposalRevision {
  sent: ProposalRevisionDraft;
  revised: DashboardProposal;
}

/**
 * Hands the dashboards the assistant composed (WB-74) to the dashboard editor. The chat does not
 * show them: it takes the user to the editor, which fills its form with the first one and offers
 * the others as buttons. Lives at the root because the chat and the editor never coexist in a tree.
 *
 * It also knows where to read the proposal the editor's form holds (WB-179), so the assistant can be
 * asked to change it: `revisionDraft` is what is sent along with the user's message. What it sends is
 * the form as it is, with the user's edits; the answer comes back as a `revision`, so the editor
 * can lay what the assistant changed over those edits (WB-207).
 */
@Injectable({ providedIn: 'root' })
export class DashboardProposalsService {
  private readonly _proposals = signal<DashboardProposal[]>([]);
  readonly proposals = this._proposals.asReadonly();
  /** Bumped on every offer, so an editor already open refills even when the list looks the same. */
  private readonly _batch = signal(0);
  readonly batch = this._batch.asReadonly();
  /** Where the editor's form can be read as a proposal, until it is saved or the editor is left. */
  private readonly _open = signal<Signal<ProposalRevisionDraft | null> | null>(null);
  /** What the assistant was last sent, to tell what it changed from what it was given. */
  private sent: ProposalRevisionDraft | null = null;
  private readonly _revision = signal<ProposalRevision | null>(null);
  /** Set by the offer of a revision, cleared by any other: the editor merges it instead of refilling. */
  readonly revision = this._revision.asReadonly();

  /** The open proposal as the assistant takes it: the form as it is now. Kinds and query previews are recomputed on the server. */
  readonly revisionDraft = computed<ProposalRevisionDraft | null>(() => this._open()?.() ?? null);

  offer(proposals: DashboardProposal[]): void {
    this._proposals.set(proposals);
    this._revision.set(null);
    this._batch.update((n) => n + 1);
  }

  /** The assistant revised the open proposal: it is the only one now, and the editor merges it with its form. */
  offerRevision(revised: DashboardProposal): void {
    const sent = this.sent;
    this._proposals.set([revised]);
    this._revision.set(sent ? { sent, revised } : null);
    this._batch.update((n) => n + 1);
  }

  /** The open proposal, to send to the assistant: a copy of it is kept to read the answer against. */
  takeRevisionDraft(): ProposalRevisionDraft | null {
    const draft = this.revisionDraft();
    this.sent = draft && (JSON.parse(JSON.stringify(draft)) as ProposalRevisionDraft);
    return draft;
  }

  clear(): void {
    this._proposals.set([]);
    this._revision.set(null);
  }

  /** Called by the editor with where its form reads as a proposal, and with null when it is saved or left. */
  setOpen(form: Signal<ProposalRevisionDraft | null> | null): void {
    this._open.set(form);
  }
}
